import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  FileText, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Sparkles, 
  CheckCircle2, 
  AlertTriangle,
  TrendingUp,
  BarChart2
} from 'lucide-react';

export const KeywordDensityChecker = () => {
  const { withAd, toast } = useAdRedirect();
  const [textInput, setTextInput] = useState(`Digital marketing is an essential component of modern business strategy. In today's digital landscape, effective digital marketing ensures brand visibility, customer engagement, and consistent revenue growth. To succeed in digital marketing, businesses must focus on SEO, content creation, and data analytics. Digital marketing campaigns that leverage these core principles consistently outperform traditional marketing channels.`);
  const [targetKeyword, setTargetKeyword] = useState('digital marketing');

  // Client-side analytics calculation
  const calculateMetrics = () => {
    const rawText = textInput.trim();
    if (!rawText) return { wordCount: 0, charCount: 0, keywordCount: 0, density: 0, nGrams: [] };

    const words = rawText.replace(/[^\w\s]/gi, '').split(/\s+/).filter(Boolean);
    const wordCount = words.length;
    const charCount = rawText.length;

    // Target keyword occurrences
    const keywordLower = targetKeyword.toLowerCase().trim();
    let keywordCount = 0;
    if (keywordLower) {
      // Regex match for exact phrase
      const escaped = keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
      const matches = rawText.match(regex);
      keywordCount = matches ? matches.length : 0;
    }

    // Density calculation
    const density = wordCount > 0 ? ((keywordCount * (keywordLower ? keywordLower.split(' ').length : 1)) / wordCount) * 100 : 0;

    // N-gram frequency extraction (2-word & 3-word combinations)
    const nGramCounts = {};
    for (let i = 0; i < words.length - 1; i++) {
      const biGram = `${words[i].toLowerCase()} ${words[i+1].toLowerCase()}`;
      nGramCounts[biGram] = (nGramCounts[biGram] || 0) + 1;
    }

    const nGrams = Object.entries(nGramCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    return { wordCount, charCount, keywordCount, density: density.toFixed(2), nGrams };
  };

  const metrics = calculateMetrics();

  const handleReset = () => {
    setTextInput('');
    setTargetKeyword('');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Keyword Density Checker - Prevent Stuffing" 
        description="Analyze article keyword density, word count, and n-gram repetitions. Optimize content for SEO without keyword stuffing risks."
        keywords="keyword density checker, keyword density tool, seo word count, n-gram analyzer, prevent keyword stuffing, content optimizer"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Keyword Density Checker</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Keyword Density Checker & N-Gram Analyzer
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Analyze article text for target keyword density percentages and over-optimized n-gram phrasing to prevent search engine penalty triggers.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Input Text & Focus Keyword */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} style={{ color: 'var(--primary)' }} /> Article Content Text
              </h3>
              <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RotateCcw size={14} /> Clear All
              </button>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Target Focus Keyword</label>
              <input 
                type="text" 
                value={targetKeyword} 
                onChange={(e) => setTargetKeyword(e.target.value)}
                placeholder="e.g. digital marketing"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', marginBottom: '20px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Paste Article Body</label>
              <textarea 
                rows="10"
                value={textInput} 
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Paste your blog post or website copy here..."
                style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>
          </div>

        </div>

        {/* Right Side: Metrics Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChart2 size={18} style={{ color: '#10b981' }} /> Live Analytics Audit Dashboard
            </h3>

            {/* Top Stat Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Words</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)' }}>{metrics.wordCount}</div>
              </div>

              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Characters</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)' }}>{metrics.charCount}</div>
              </div>

              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Occurrences</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--primary)' }}>{metrics.keywordCount}</div>
              </div>

              <div style={{ background: metrics.density > 4 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)', padding: '16px', borderRadius: '14px', border: `1px solid ${metrics.density > 4 ? '#ef4444' : '#10b981'}`, textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: metrics.density > 4 ? '#ef4444' : '#10b981', marginBottom: '4px' }}>Density %</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: metrics.density > 4 ? '#ef4444' : '#10b981' }}>{metrics.density}%</div>
              </div>
            </div>

            {/* Density Evaluation Warning / Success */}
            {metrics.wordCount > 0 && targetKeyword && (
              <div style={{ marginBottom: '28px', padding: '16px', borderRadius: '14px', background: metrics.density > 4 ? 'rgba(239, 68, 68, 0.05)' : 'rgba(16, 185, 129, 0.05)', border: `1px solid ${metrics.density > 4 ? '#ef4444' : '#10b981'}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
                {metrics.density > 4 ? (
                  <>
                    <AlertTriangle size={24} style={{ color: '#ef4444', flexShrink: 0 }} />
                    <div style={{ fontSize: '0.85rem', color: '#ef4444', lineHeight: 1.4 }}>
                      <strong>High Stuffing Risk:</strong> Your keyword density is <strong>{metrics.density}%</strong>. Search engine algorithms (Google SpamBrain) may penalize this webpage for repetitive keyword stuffing. We advise lowering density below 3%.
                    </div>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={24} style={{ color: '#10b981', flexShrink: 0 }} />
                    <div style={{ fontSize: '0.85rem', color: '#10b981', lineHeight: 1.4 }}>
                      <strong>Optimal Density Score:</strong> Your keyword density is perfectly optimized at <strong>{metrics.density}%</strong>. It stays within the recommended 1% to 3% natural semantic threshold.
                    </div>
                  </>
                )}
              </div>
            )}

            {/* N-Gram Repetitions List */}
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TrendingUp size={16} style={{ color: 'var(--primary)' }} /> Top 2-Word Phrasal Repetitions (Bi-Grams)
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {metrics.nGrams.length > 0 ? metrics.nGrams.map(([gram, count], idx) => (
                  <div key={idx} style={{ background: 'var(--bg-secondary)', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>&quot;{gram}&quot;</span>
                    <span style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.75rem' }}>{count}x</span>
                  </div>
                )) : (
                  <div style={{ gridColumn: 'span 2', textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    Paste article text above to extract phrasing repetitions.
                  </div>
                )}
              </div>
            </div>

            <AdToast message={toast} />
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="keyword-density-checker" />
    </div>
  );
};

export default KeywordDensityChecker;
